/**
 * The seller's offers: propose a discount on your own products, choose who
 * pays for it, and track it through review to live.
 *
 * Nothing here changes a price by itself. An offer is sent to Terra Shoppy,
 * and only once approved does the storefront sell at the offer price.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgePercent, Clock, Handshake, Plus, Store, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  ApiClientError,
  type OfferPhase,
  type ProductListItem,
  type SellerOffer,
} from '@terra/api-client';
import { OFFER_PLATFORM_SHARE_BPS, type VendorOfferRequest } from '@terra/contracts';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  toast,
} from '@terra/ui';

import { api } from '../lib/api';

const rupees = (p: number) => `₹${(p / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;
const when = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

const PHASE: Record<OfferPhase, { label: string; tone: BadgeTone }> = {
  PENDING_APPROVAL: { label: 'Waiting for approval', tone: 'warning' },
  SCHEDULED: { label: 'Approved · starts soon', tone: 'info' },
  LIVE: { label: 'Live', tone: 'success' },
  ENDED: { label: 'Ended', tone: 'neutral' },
  REJECTED: { label: 'Not approved', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

const TABS: Array<{ key: 'ALL' | OfferPhase; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING_APPROVAL', label: 'Pending' },
  { key: 'LIVE', label: 'Live' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'REJECTED', label: 'Not approved' },
  { key: 'ENDED', label: 'Ended' },
];

const FUNDING_COPY = {
  SELLER: {
    title: 'I pay the discount',
    body: 'You fund the whole discount. Fastest to approve.',
    icon: Store,
  },
  SPLIT: {
    title: '50-50 with Terra Shoppy',
    body: 'Terra Shoppy pays half the discount, added to your payout. Needs approval.',
    icon: Handshake,
  },
} as const;

/** The money for one unit under an offer, for the preview. */
function split(pricePaise: number, type: 'PERCENTAGE' | 'FLAT', amount: number, shareBps: number) {
  const discount = Math.min(
    pricePaise,
    Math.max(0, type === 'PERCENTAGE' ? Math.round((pricePaise * amount) / 10_000) : amount),
  );
  const platform = Math.round((discount * shareBps) / 10_000);
  return { discount, offerPrice: pricePaise - discount, platform, seller: discount - platform };
}

/** <input type="datetime-local"> value for a date, in the browser's time zone. */
const toLocalInput = (d: Date) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return z.toISOString().slice(0, 16);
};

interface DraftItem {
  productId: string;
  title: string;
  pricePaise: number;
  discountType: 'PERCENTAGE' | 'FLAT';
  /** What the seller typed: percent, or rupees. */
  value: string;
}

interface Draft {
  id?: string;
  title: string;
  startsAt: string;
  endsAt: string;
  funding: 'SELLER' | 'SPLIT';
  sellerNote: string;
  items: DraftItem[];
}

const blankDraft = (): Draft => {
  const start = new Date(Date.now() + 60 * 60_000);
  const end = new Date(start.getTime() + 3 * 86_400_000);
  return {
    title: '',
    startsAt: toLocalInput(start),
    endsAt: toLocalInput(end),
    funding: 'SELLER',
    sellerNote: '',
    items: [],
  };
};

const draftFrom = (o: SellerOffer): Draft => ({
  id: o.id,
  title: o.title,
  startsAt: toLocalInput(new Date(o.startsAt)),
  endsAt: toLocalInput(new Date(o.endsAt)),
  funding: o.funding === 'SPLIT' ? 'SPLIT' : 'SELLER',
  sellerNote: o.sellerNote ?? '',
  items: o.items.map((i) => ({
    productId: i.productId,
    title: i.title,
    pricePaise: i.pricePaise,
    discountType: i.discountType,
    // bps → % and paise → ₹ are both ÷100.
    value: String(i.discountAmount / 100),
  })),
});

function toRequest(d: Draft): VendorOfferRequest {
  return {
    title: d.title.trim(),
    startsAt: new Date(d.startsAt).toISOString(),
    endsAt: new Date(d.endsAt).toISOString(),
    funding: d.funding,
    ...(d.sellerNote.trim() ? { sellerNote: d.sellerNote.trim() } : {}),
    items: d.items.map((i) => ({
      productId: i.productId,
      discountType: i.discountType,
      discountAmount: Math.round(Number(i.value) * 100),
    })),
  };
}

/** What is wrong with a draft, in the order the seller should fix it; null when it can be sent. */
function draftProblem(d: Draft): string | null {
  if (d.title.trim().length < 3) return 'Give the offer a name (at least 3 letters)';
  if (!d.startsAt || !d.endsAt) return 'Choose when the offer starts and ends';
  if (new Date(d.endsAt) <= new Date(d.startsAt)) return 'The offer must end after it starts';
  if (new Date(d.endsAt) <= new Date()) return 'The end time has already passed';
  if (d.items.length === 0) return 'Add at least one product';
  for (const i of d.items) {
    const n = Number(i.value);
    if (!i.value || !Number.isFinite(n) || n <= 0) return `Enter a discount for "${i.title}"`;
    if (i.discountType === 'PERCENTAGE' && n >= 100)
      return `"${i.title}": the discount must be less than 100%`;
    if (i.discountType === 'FLAT' && Math.round(n * 100) >= i.pricePaise)
      return `"${i.title}": the discount must be less than the price`;
  }
  return null;
}

/** How a price is built, top to bottom, so sellers see where their offer sits. */
function DiscountLadder() {
  const steps = [
    { label: 'MRP', note: 'Printed price, shown struck through' },
    { label: 'Your selling price', note: 'What you list the product at' },
    {
      label: 'Offer (this page)',
      note: 'One best offer per product — offers never stack',
      hl: true,
    },
    { label: 'Coupons', note: 'Terra Shoppy and store coupons apply on top at checkout' },
    { label: 'Customer pays', note: 'GST is charged on this final price' },
  ];
  return (
    <Card>
      <CardBody>
        <p className="text-ink-muted text-xs font-bold uppercase tracking-wider">
          How a price is built
        </p>
        <ol className="mt-3 grid gap-2 sm:grid-cols-5">
          {steps.map((s, i) => (
            <li
              key={s.label}
              className={`rounded-lg border p-3 ${s.hl ? 'border-gold-300 bg-gold-50' : 'border-border bg-surface-subtle'}`}
            >
              <p className="text-ink-muted text-[11px] font-bold">Step {i + 1}</p>
              <p className="text-ink text-sm font-bold">{s.label}</p>
              <p className="text-ink-muted mt-1 text-xs">{s.note}</p>
            </li>
          ))}
        </ol>
      </CardBody>
    </Card>
  );
}

function OfferForm({ initial, onClose }: { initial: Draft; onClose: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(initial);
  const [q, setQ] = useState('');
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((s) => ({ ...s, [k]: v }));

  const products = useQuery({
    queryKey: ['vendor-products-approved'],
    queryFn: () => api.products.myProducts({ pageSize: 100, status: 'APPROVED' }),
  });
  const candidates = useMemo(() => {
    const list: ProductListItem[] = products.data?.data ?? [];
    const chosen = new Set(d.items.map((i) => i.productId));
    const needle = q.trim().toLowerCase();
    return list
      .filter((p) => p.isActive && !p.deletedAt && p.variants.length > 0 && !chosen.has(p.id))
      .filter((p) => !needle || p.title.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [products.data, d.items, q]);

  const shareBps = OFFER_PLATFORM_SHARE_BPS[d.funding];
  const problem = draftProblem(d);

  const save = useMutation({
    mutationFn: () =>
      d.id
        ? api.marketing.resubmitOffer(d.id, toRequest(d))
        : api.marketing.submitOffer(toRequest(d)),
    onSuccess: () => {
      toast.success('Offer sent to Terra Shoppy for approval');
      void qc.invalidateQueries({ queryKey: ['vendor-offers'] });
      onClose();
    },
    onError: (e) => toast.error(errMsg(e, 'Could not send the offer')),
  });

  const updateItem = (id: string, patch: Partial<DraftItem>) =>
    set(
      'items',
      d.items.map((i) => (i.productId === id ? { ...i, ...patch } : i)),
    );

  return (
    <Card>
      <CardBody className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-lg font-bold">
            {d.id ? 'Edit and resend offer' : 'New offer'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* 1. Name and dates */}
        <section className="grid gap-4 sm:grid-cols-3">
          <label className="block text-sm sm:col-span-3">
            <span className="text-ink font-semibold">Offer name</span>
            <Input
              value={d.title}
              maxLength={120}
              placeholder="e.g. Weekend Kurta Sale"
              onChange={(e) => set('title', e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-ink font-semibold">Starts</span>
            <Input
              type="datetime-local"
              value={d.startsAt}
              onChange={(e) => set('startsAt', e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-ink font-semibold">Ends</span>
            <Input
              type="datetime-local"
              value={d.endsAt}
              onChange={(e) => set('endsAt', e.target.value)}
            />
          </label>
        </section>

        {/* 2. Who pays */}
        <section>
          <p className="text-ink text-sm font-semibold">Who pays for the discount?</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {(['SELLER', 'SPLIT'] as const).map((f) => {
              const c = FUNDING_COPY[f];
              const Icon = c.icon;
              const on = d.funding === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => set('funding', f)}
                  className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${on ? 'border-gold-500 bg-gold-50 ring-gold-200 ring-2' : 'border-border hover:bg-surface-subtle bg-white'}`}
                >
                  <Icon className={`mt-0.5 h-5 w-5 ${on ? 'text-gold-700' : 'text-ink-muted'}`} />
                  <span>
                    <span className="text-ink block text-sm font-bold">{c.title}</span>
                    <span className="text-ink-muted block text-xs">{c.body}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* 3. Products */}
        <section className="space-y-3">
          <p className="text-ink text-sm font-semibold">Products and discount</p>
          {d.items.length > 0 && (
            <div className="border-border overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-surface-subtle text-ink-muted text-left text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2">Product</th>
                    <th className="px-3 py-2">Discount</th>
                    <th className="px-3 py-2 text-right">Offer price</th>
                    <th className="px-3 py-2 text-right">You pay</th>
                    <th className="px-3 py-2 text-right">Terra Shoppy pays</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((i) => {
                    // % → basis points and ₹ → paise are both ×100.
                    const amount = Math.round(Number(i.value || 0) * 100);
                    const m = split(i.pricePaise, i.discountType, amount, shareBps);
                    return (
                      <tr key={i.productId} className="border-border border-t">
                        <td className="px-3 py-2">
                          <p className="text-ink font-medium">{i.title}</p>
                          <p className="text-ink-muted text-xs">
                            Selling at {rupees(i.pricePaise)}
                          </p>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <select
                              className="border-border rounded-md border px-2 py-1.5 text-sm"
                              value={i.discountType}
                              onChange={(e) =>
                                updateItem(i.productId, {
                                  discountType: e.target.value as DraftItem['discountType'],
                                })
                              }
                            >
                              <option value="PERCENTAGE">% off</option>
                              <option value="FLAT">₹ off</option>
                            </select>
                            <input
                              className="border-border w-24 rounded-md border px-2 py-1.5 text-sm"
                              inputMode="decimal"
                              value={i.value}
                              placeholder={i.discountType === 'PERCENTAGE' ? '10' : '100'}
                              onChange={(e) =>
                                updateItem(i.productId, {
                                  value: e.target.value.replace(/[^\d.]/g, ''),
                                })
                              }
                            />
                          </div>
                        </td>
                        <td className="text-ink px-3 py-2 text-right font-bold">
                          {rupees(m.offerPrice)}
                        </td>
                        <td className="px-3 py-2 text-right">{rupees(m.seller)}</td>
                        <td className="text-state-success px-3 py-2 text-right">
                          {rupees(m.platform)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Remove"
                            onClick={() =>
                              set(
                                'items',
                                d.items.filter((x) => x.productId !== i.productId),
                              )
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-border rounded-lg border border-dashed p-3">
            <Input
              placeholder="Search your approved products to add"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {products.isLoading ? (
              <div className="py-3">
                <Spinner />
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-ink-muted mt-2 text-xs">
                {q
                  ? 'No approved product matches.'
                  : 'All your approved products are already added.'}
              </p>
            ) : (
              <ul className="divide-border mt-2 divide-y">
                {candidates.map((p) => {
                  const price = Math.min(...p.variants.map((v) => v.pricePaise));
                  return (
                    <li key={p.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-ink text-sm">
                        {p.title} <span className="text-ink-muted text-xs">· {rupees(price)}</span>
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          set('items', [
                            ...d.items,
                            {
                              productId: p.id,
                              title: p.title,
                              pricePaise: price,
                              discountType: 'PERCENTAGE',
                              value: '',
                            },
                          ])
                        }
                      >
                        <Plus className="h-4 w-4" /> Add
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="text-ink-muted text-xs">
            Prices shown are for each product&apos;s lowest-priced variant. A percentage discount
            applies to every variant; a ₹ discount takes the same amount off each.
          </p>
        </section>

        {/* 4. Note */}
        <label className="block text-sm">
          <span className="text-ink font-semibold">Note for the Terra Shoppy team (optional)</span>
          <textarea
            className="border-border mt-1 w-full rounded-md border p-2 text-sm"
            rows={2}
            maxLength={500}
            value={d.sellerNote}
            placeholder="e.g. Festive clearance; stock of 200 units ready"
            onChange={(e) => set('sellerNote', e.target.value)}
          />
        </label>

        <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className={`text-sm ${problem ? 'text-state-danger' : 'text-ink-muted'}`}>
            {problem ?? 'Prices change only after Terra Shoppy approves this offer.'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!!problem} loading={save.isPending} onClick={() => save.mutate()}>
              Send for approval
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function OfferCard({ o, onEdit }: { o: SellerOffer; onEdit: () => void }) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => api.marketing.cancelOffer(o.id),
    onSuccess: () => {
      toast.success('Offer cancelled');
      void qc.invalidateQueries({ queryKey: ['vendor-offers'] });
    },
    onError: (e) => toast.error(errMsg(e, 'Could not cancel the offer')),
  });
  const phase = PHASE[o.phase];
  const canEdit = o.status === 'PENDING_APPROVAL' || o.status === 'REJECTED';
  const canCancel =
    o.status === 'PENDING_APPROVAL' || (o.status === 'APPROVED' && o.phase !== 'ENDED');

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-ink text-base font-bold">{o.title}</p>
            <p className="text-ink-muted mt-0.5 flex items-center gap-1 text-xs">
              <Clock className="h-3.5 w-3.5" /> {when(o.startsAt)} → {when(o.endsAt)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={o.funding === 'SPLIT' ? 'gold' : 'neutral'}>
              {o.funding === 'SPLIT' ? '50-50 with Terra Shoppy' : 'Seller-funded'}
            </Badge>
            <Badge tone={phase.tone} dot>
              {phase.label}
            </Badge>
          </div>
        </div>

        {o.status === 'REJECTED' && o.reviewNote && (
          <p className="bg-state-danger-bg text-state-danger rounded-md p-2 text-sm">
            Reason: {o.reviewNote}
          </p>
        )}
        {o.status === 'APPROVED' && o.reviewNote && (
          <p className="bg-state-info-bg text-state-info rounded-md p-2 text-sm">
            Note: {o.reviewNote}
          </p>
        )}

        <ul className="divide-border border-border divide-y rounded-lg border">
          {o.items.map((i) => (
            <li
              key={i.productId}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="text-ink font-medium">{i.title}</span>
              <span className="flex items-center gap-3">
                <span className="text-ink-muted line-through">{rupees(i.pricePaise)}</span>
                <span className="text-ink font-bold">{rupees(i.offerPricePaise)}</span>
                <Badge tone="red">
                  {i.discountType === 'PERCENTAGE'
                    ? `${i.discountAmount / 100}% off`
                    : `${rupees(i.discountAmount)} off`}
                </Badge>
                {i.platformFundsPaise > 0 && (
                  <span className="text-state-success text-xs">
                    Terra Shoppy pays {rupees(i.platformFundsPaise)}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>

        {(canEdit || canCancel) && (
          <div className="flex justify-end gap-2">
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                loading={cancel.isPending}
                onClick={() => {
                  if (window.confirm('Cancel this offer? Prices go back to normal.'))
                    cancel.mutate();
                }}
              >
                Cancel offer
              </Button>
            )}
            {canEdit && (
              <Button size="sm" onClick={onEdit}>
                {o.status === 'REJECTED' ? 'Fix and resend' : 'Edit'}
              </Button>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function OffersPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('ALL');
  const [editing, setEditing] = useState<Draft | null>(null);
  const offers = useQuery({ queryKey: ['vendor-offers'], queryFn: () => api.marketing.myOffers() });
  const list = offers.data ?? [];
  const shown = tab === 'ALL' ? list : list.filter((o) => o.phase === tab);
  const count = (k: OfferPhase) => list.filter((o) => o.phase === k).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Offers"
        description="Run a discount on your products. Pay for it yourself, or ask Terra Shoppy to share half."
        actions={
          !editing && (
            <Button onClick={() => setEditing(blankDraft())}>
              <Plus className="h-4 w-4" /> New offer
            </Button>
          )
        }
      />

      <DiscountLadder />

      {editing && (
        <OfferForm key={editing.id ?? 'new'} initial={editing} onClose={() => setEditing(null)} />
      )}

      <div className="border-border flex gap-1 overflow-x-auto border-b">
        {TABS.map((t) => {
          const n = t.key === 'ALL' ? list.length : count(t.key);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold ${tab === t.key ? 'border-red-600 text-red-700' : 'text-ink-muted hover:text-ink border-transparent'}`}
            >
              {t.label} {n > 0 && <span className="ml-1 text-xs">({n})</span>}
            </button>
          );
        })}
      </div>

      {offers.isLoading ? (
        <Spinner />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<BadgePercent className="h-8 w-8" />}
          title={tab === 'ALL' ? 'No offers yet' : 'Nothing here'}
          description="Create an offer to put your products on sale. Terra Shoppy reviews every offer before it goes live."
        />
      ) : (
        <div className="space-y-4">
          {shown.map((o) => (
            <OfferCard
              key={o.id}
              o={o}
              onEdit={() => {
                setEditing(draftFrom(o));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
